import {findIndex, last} from 'lodash';
import Token from 'markdown-it/lib/token';
import React, {ComponentType, FC, ReactNode} from 'react';

import {TokenNestingsEnum} from '../../../src/services/markdown';
import {MarkdownTokenFormatterProps, NodeTransform} from './markdownFormatterTypes';

/*
 * Props.
 */

interface MarkdownTokensFormatterProps {
  formatter: ComponentType<MarkdownTokenFormatterProps>;
  tokens: ReadonlyArray<Token>;
  transform?: NodeTransform;
  onLinkClick?: (link: string, shiftKey?: boolean) => void;
}

/*
 * Component.
 */

export const MarkdownTokensFormatter: FC<MarkdownTokensFormatterProps> = (props) => (
  <>{renderTokens(props, props.tokens, 0)}</>
);

function findClosingTokenIndex(tokens: readonly Token[], currentIndex: number): number {
  let level = 0;
  return findIndex(
    tokens,
    (t) => {
      if (t.nesting === TokenNestingsEnum.OPENING) {
        level++;
      } else if (t.nesting === TokenNestingsEnum.CLOSING) {
        level--;
      }

      return level === 0;
    },
    currentIndex,
  );
}

/*
 * Markdown logic.
 */

/* eslint no-continue: 0 */
function renderTokens(props: MarkdownTokensFormatterProps, tokens: ReadonlyArray<Token>, depth: number) {
  const result: Array<ReactNode> = [];
  let previousToken: Token | undefined;

  for (let index = 0; index < tokens.length; index++) {
    const token = tokens[index];

    // Ignore hidden tokens.
    if (token.hidden) {
      continue;
    }

    // If this is not a nested token, render it.
    if (token.nesting === TokenNestingsEnum.SELF_CLOSING) {
      result.push(renderToken(props, token, previousToken, depth));
      previousToken = token;
      continue;
    }

    // Otherwise, find the closing token.
    const closingTokenIndex = findClosingTokenIndex(tokens, index);

    // We need a closing token.
    if (closingTokenIndex < 0) {
      throw new Error('Closing token not found.');
    }

    // Render this token with all its children.
    const children = tokens.slice(index + 1, closingTokenIndex);
    result.push(renderToken(props, token, previousToken, depth, children));
    // We only care about the last child's content
    previousToken = {...token, content: last(children)?.content ?? ''};
    index = closingTokenIndex;
  }

  return result;
}

function renderToken(
  props: MarkdownTokensFormatterProps,
  token: Token,
  previousToken: Token | undefined,
  depth: number,
  children?: ReadonlyArray<Token>,
): ReactNode {
  const Formatter = props.formatter;

  // Render the children.
  const childrenRender = renderChildren(props, token, depth, previousToken, children);
  const key = makeKey();

  // Render the token.
  return (
    <Formatter
      key={key}
      token={token}
      previousTokenType={previousToken?.type}
      depth={depth}
      onLinkClick={props.onLinkClick}
    >
      {childrenRender}
    </Formatter>
  );
}

function renderChildren(
  props: MarkdownTokensFormatterProps,
  token: Token,
  depth: number,
  previousToken: Token | undefined,
  children?: ReadonlyArray<Token>,
): ReactNode {
  // If it's an inline token, render its children.
  if (token.type === 'inline') {
    return renderTokens(props, token.children, depth + 1);
  }

  // If this token is self-closing, transform its content.
  if (token.nesting === TokenNestingsEnum.SELF_CLOSING) {
    return (
      (token.type !== 'fence' && props.transform && props.transform(token.content, previousToken)) ||
      token.content
    );
  }

  // Otherwise, render the children we found in the collection.
  return (children && renderTokens(props, children, depth + 1)) || null;
}

/*
 * Key ID.
 */

let lastKeyId = 0;
function makeKey() {
  return `token-${++lastKeyId}`;
}
